import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Divider
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

const ProModal = ({
  open,
  onClose,
  title,
  eyebrow,
  subtitle,
  icon,
  children,
  actions,
  maxWidth = 'sm',
  fullWidth = true,
  PaperProps = {},
  contentProps = {},
  actionsProps = {},
  hideDivider = false,
  headerStyles = {},
  disableCloseButton = false,
  ...rest
}) => {
  const theme = useTheme();
  const defaultPaperStyles = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 0,
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    overflow: 'hidden',
    position: 'relative'
  };

  const mergedPaperProps = {
    ...PaperProps,
    sx: {
      ...defaultPaperStyles,
      ...(PaperProps?.sx || {})
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={mergedPaperProps}
      {...rest}
    >
      <DialogTitle
        component="div"
        sx={{
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          ...headerStyles
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {icon && (
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.primary.main
              }}
            >
              {typeof icon === 'function' ? icon() : icon}
            </Box>
          )}
          <Box>
            {eyebrow && (
              <Typography
                variant="overline"
                sx={{
                  display: 'block',
                  letterSpacing: 3,
                  color: alpha('#fff', theme.palette.mode === 'dark' ? 0.8 : 0.6)
                }}
              >
                {eyebrow}
              </Typography>
            )}
            {title && (
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        {!disableCloseButton && (
          <IconButton
            onClick={onClose}
            sx={{
              borderRadius: 1,
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08)
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      {!hideDivider && <Divider sx={{ opacity: 0.5 }} />}

      <DialogContent
        sx={{ px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3 }}
        {...contentProps}
      >
        {children}
      </DialogContent>

      {actions && (
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`
          }}
          {...actionsProps}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

ProModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  eyebrow: PropTypes.string,
  subtitle: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  children: PropTypes.node,
  actions: PropTypes.node,
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', false]),
  fullWidth: PropTypes.bool,
  PaperProps: PropTypes.object,
  contentProps: PropTypes.object,
  actionsProps: PropTypes.object,
  hideDivider: PropTypes.bool,
  headerStyles: PropTypes.object,
  disableCloseButton: PropTypes.bool
};

export default ProModal;
